import React from 'react';
import Header from './Header';
import Footer from './Footer';
import CookieConsent from '../CookieConsent';
import SEO from '../SEO';
import { homeSEO } from '../../constants/seo';

/**
 * Props for the Layout component
 */
interface LayoutProps {
  children: React.ReactNode;
  seo?: {
    title?: string;
    description?: string;
    canonical?: string;
    ogImage?: string;
    ogType?: 'website' | 'article';
    twitterCard?: 'summary' | 'summary_large_image';
    noIndex?: boolean;
    structuredData?: Record<string, any>;
  };
}

/**
 * Layout component that wraps content with Header and Footer
 * 
 * This component provides a consistent layout structure for all pages
 * by including the header and footer components and handling the main content area
 * 
 * @param {LayoutProps} props - Component props
 * @returns {JSX.Element} The rendered Layout component
 */
const Layout: React.FC<LayoutProps> = ({ children, seo = homeSEO }) => {
  try {
    // Log component render
    console.log('Rendering Layout component');
    
    return (
      <div className="flex flex-col min-h-screen">
        <SEO 
          title={seo.title}
          description={seo.description}
          canonical={seo.canonical}
          ogImage={seo.ogImage}
          ogType={seo.ogType}
          twitterCard={seo.twitterCard}
          noIndex={seo.noIndex}
          structuredData={seo.structuredData}
        />
        <Header />
        
        {/* Main content area with padding for fixed header */}
        <main className="flex-grow">
          {children}
        </main>
        
        <Footer />
        <CookieConsent />
      </div>
    );
  } catch (error) {
    // Log any errors that occur during rendering
    console.error('Error rendering Layout component:', error);
    
    // Fallback UI in case of error
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 dark:bg-red-900">
        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
            Layout Error
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            An error occurred while rendering the page layout.
            Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }
};

export default Layout;
